import { PlatformLocation } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BsVerions, setTheme } from 'ngx-bootstrap/utils';
import { MatomoTracker } from 'ngx-matomo-client';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AuthService } from './core/services/auth.service';
import { LoaderService } from './core/services/loader.service';
import { Title } from '@angular/platform-browser';
import { AppService } from './core/services/app.service';
import { TitleService } from './core/services/title.service';
import { HolidayThemeService } from './core/services/holidayTheme.service';


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    showLoader;
    
    // Delegates preview detection to AppService to avoid duplicated logic across components.

    /**
     * Expose AppService preview detection to the template (used in app.component.html)
     */
    isPrintPreview(): boolean {
        return this._appService.isPrintPreview();
    }


    private _subscribedObservable: Subscription = new Subscription();

    constructor(
        private _loaderService: LoaderService,
        public authService: AuthService,
        translate: TranslateService,
        private _router: Router,
        private _location: PlatformLocation,
        private _matomoTracker: MatomoTracker,
        private _titleServiceFromBrowser: Title, // Inject Title service
        private _activatedRoute: ActivatedRoute, // Inject ActivatedRoute service
        private _appService: AppService,
        private _titleService: TitleService,
        private holidayTheme: HolidayThemeService 
    ) {
        setTheme(BsVerions.isBs4); // 'bs4', bs5

        translate.addLangs(['en', 'nl']);
        translate.setDefaultLang(environment.preferredLanguage);
        translate.reloadLang('en');
        translate.reloadLang('nl');

        this.resetBrowserState();

    }

    /**
     * Re-set browser history state
     */
    resetBrowserState() {
        history.pushState(null, null, window.location.href);
        if (this.authService.isLoggedIn()) {
            /* Listening back button */
            this._location.onPopState(() => {
                const path = window.location.pathname + window.location.search + window.location.hash;
                this._router.navigateByUrl(path);
            });
        }
    }


    ngOnInit(): void {
        this._matomoTracker.trackPageView('GPR Materials');

        // Listen to router events and update the title dynamically
        this._subscribedObservable.add(
            this._router.events.subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    this.updateTitle();
                }
            })
        );
    }


    /**
     * show loader before load all containt loads
     */
    ngAfterViewInit() {
        this._subscribedObservable.add(
            this._loaderService.loadLoader$.subscribe(showLoader => {
                this.showLoader = showLoader;
            })
        );

        this.holidayTheme.init();
    }

    

    /**
    * Update the page title based on the current route's metadata
    */
    updateTitle() {
        let route = this._activatedRoute.root;
        while (route.firstChild) {
            route = route.firstChild;
        }

        // Get the page/component title from route data
        const titleId = route.snapshot.data['titleId']; // Get titleId if available

        // Translate the page title if titleId exists
        const translatedPageTitle = this._titleService.translateTitle(titleId);

        // Get the calculation name
        this._appService.getCalculationName()
            .pipe(take(1))
            .subscribe(calculationName => {
                // Limit the calculation name to 90 characters
                const truncatedCalculationName = calculationName.length > 90 ? calculationName.slice(0, 90) : calculationName;

                // Construct the dynamic title after the calculation name is available
                const dynamicTitle = `${truncatedCalculationName} - ${translatedPageTitle}`;
                // Set the title using TitleService
                this._titleServiceFromBrowser.setTitle(dynamicTitle);
            });
    }

    /**
     * disposes the resource in the subscription
     */
    ngOnDestroy() {
        this._subscribedObservable.unsubscribe();
        this.holidayTheme.destroy();
    }
}
